import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

import type { AssetBookingDto, EventDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { EventOperationalAnalyticsSummary } from "@/components/OperationalAnalytics/OperationalAnalyticsSummaries";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Container } from "@/design-system/Container";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { Tiles } from "@/design-system/Tiles";
import { canManageEvents } from "@/helpers/permissions";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import type { CalendarData, CalendarTab } from "@/modules/calendar/constants";
import { EVENTS_TAB, RESERVATIONS_TAB } from "@/modules/calendar/constants";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EventCard } from "./components/EventCard";
import type { EventFilterValues } from "./components/EventFilterDrawer";
import { EventFilterDrawer } from "./components/EventFilterDrawer";
import { ReservationCard } from "./components/ReservationsCard";

export interface CalendarOverviewContentProps {
  search: string;
  onChangeSearch: (query: string) => void;
  activeTab: CalendarTab;
  onChangeTab: (tab: CalendarTab) => void;
  tabs: TabItem<CalendarTab>[];
  eventFilter: EventFilterValues;
  onUpdateEventFilter: (filter: EventFilterValues) => void;
  events: {
    upcoming: CalendarData<EventDto>;
    past: CalendarData<EventDto>;
  };
  reservationsSearch: string;
  onReservationsSearch: (query: string) => void;
  reservations: {
    upcoming: CalendarData<AssetBookingDto>;
    past: CalendarData<AssetBookingDto>;
  };
}
export function CalendarOverviewContent({
  tabs,
  activeTab,
  onChangeTab,
  search,
  onChangeSearch,
  eventFilter,
  onUpdateEventFilter,
  events,
  reservationsSearch,
  onReservationsSearch,
  reservations,
}: CalendarOverviewContentProps): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const postHog = usePostHog();
  const sessionUser = useSessionUser();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.calendar.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.calendar.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{
              scope: "project",
              entity: activeTab === "events" ? "events" : "bookings",
              page: "list",
              filters: {
                search: activeTab === "events" ? search : reservationsSearch,
                category: eventFilter.category,
                createdBySelf: eventFilter.createdBySelf,
                signedUpBySelf: eventFilter.signedUpBySelf,
              },
            }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.calendar.suggestion.1"),
              t("component.ask-nobi-chat-drawer.calendar.suggestion.2"),
            ]}
          />
          {activeTab === EVENTS_TAB ? (
            hasPermission(canManageEvents) && (
              <Button
                type="link"
                variant="filled"
                href={routes.events.create({
                  slug,
                })}
                onClick={() => postHog.capture("clicked_create_event")}
              >
                {t("page.calendar.button.new-event")}
              </Button>
            )
          ) : (
            <Button
              type="link"
              variant="filled"
              href={routes.bookings.list({
                slug,
              })}
            >
              {t("page.calendar.button.new-reservation")}
            </Button>
          )}
        </>
      }
    >
      <Stack gap="md">
        {activeTab === EVENTS_TAB && <EventOperationalAnalyticsSummary />}
        <Tabs.Root activeTab={activeTab} tabs={tabs} onSelectTab={onChangeTab}>
          <Tabs.Content value={EVENTS_TAB}>
            <EventTabContent
              search={search}
              onChangeSearch={onChangeSearch}
              filter={eventFilter}
              onUpdateFilter={onUpdateEventFilter}
              upcomingEvents={events.upcoming}
              pastEvents={events.past}
            />
          </Tabs.Content>
          <Tabs.Content value={RESERVATIONS_TAB}>
            <ReservationsFilterTab onSearch={onReservationsSearch} />
            <ReservationsContentTab
              upcoming={reservations.upcoming}
              past={reservations.past}
              search={reservationsSearch}
            />
          </Tabs.Content>
        </Tabs.Root>
      </Stack>
    </DocumentPage>
  );
}

interface EventsContentTabProps {
  search: string;
  onChangeSearch: (query: string) => void;
  filter: EventFilterValues;
  onUpdateFilter: (filter: EventFilterValues) => void;
  upcomingEvents: CalendarData<EventDto>;
  pastEvents: CalendarData<EventDto>;
}
function EventTabContent({
  search,
  onChangeSearch,
  filter,
  onUpdateFilter,
  upcomingEvents,
  pastEvents,
}: EventsContentTabProps): React.ReactNode {
  const [isFilterOpened, filterHandler] = useBool(false);
  const isLoading = upcomingEvents.isLoading || pastEvents.isLoading;

  const { t } = useTranslation();
  const amountActiveFilters = Object.entries(filter).filter(([key, value]) => {
    if (key === "category") {
      return !!value;
    }

    if (key === "createdBySelf" || key === "signedUpBySelf") {
      return value;
    }

    return false;
  }).length;

  return (
    <>
      <Inline gap="sm" align="center">
        <SearchInput
          data-testid="search-input"
          width="wide"
          placeholder={t("page.calendar.search-events.placeholder")}
          value={search}
          onChange={(e) => onChangeSearch(e.target.value)}
        />
        <Button icon={<FilterFunnel01Icon />} variant="soft" onClick={filterHandler.toggle}>
          <Inline as="span" gap="sm" align="center">
            {t("common.action.filter")}
            {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
          </Inline>
        </Button>
      </Inline>
      {isLoading && <FullSizeLoader withPadding />}
      {!isLoading && (
        <Box padding={{ top: "md" }}>
          <Stack gap="md">
            <span className="text-body-emphasis">{t("page.calendar.events.section.upcoming")}</span>
            {!upcomingEvents.items.length && (
              <span className="text-caption">
                {search ? t("page.calendar.events.search.no-results") : t("page.calendar.events.no-upcoming-events")}
              </span>
            )}
            {upcomingEvents.items.length > 0 && (
              <>
                <Container fullWidth>
                  <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
                    {upcomingEvents.items.map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </Tiles>
                </Container>
                {upcomingEvents.isLoadingMore && (
                  <Inline justify="center" wrap={false}>
                    <Spinner size="xs" />
                  </Inline>
                )}
                {!upcomingEvents.isLoadingMore && upcomingEvents.hasMore && (
                  <Button variant="subtle" onClick={upcomingEvents.loadMore}>
                    {t("page.calendar.events.section.show-more")}
                  </Button>
                )}
              </>
            )}
            <span className="text-body-emphasis">{t("page.calendar.events.section.past")}</span>
            {!pastEvents.items.length && (
              <span className="text-caption">
                {search ? t("page.calendar.events.search.no-results") : t("page.calendar.events.no-past-events")}
              </span>
            )}
            {pastEvents.items.length > 0 && (
              <>
                <Container fullWidth>
                  <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
                    {pastEvents.items.map((event) => (
                      <EventCard key={event.id} event={event} isPast />
                    ))}
                  </Tiles>
                </Container>
                {pastEvents.isLoadingMore && (
                  <Inline justify="center" wrap={false}>
                    <Spinner size="xs" />
                  </Inline>
                )}
                {!pastEvents.isLoadingMore && pastEvents.hasMore && (
                  <Button variant="subtle" onClick={pastEvents.loadMore}>
                    {t("page.calendar.events.section.show-more")}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}

      <EventFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterHandler.set}
        filter={filter}
        onUpdate={(newFilter) => onUpdateFilter({ ...filter, ...newFilter })}
      />
    </>
  );
}

interface ReservationsFilterTabProps {
  onSearch: (query: string) => void;
}

function ReservationsFilterTab({ onSearch }: ReservationsFilterTabProps): React.ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedQuery = useDebounceValue(search.toLowerCase().trim(), 300);

  useEffect(() => {
    onSearch(debouncedQuery);
  }, [debouncedQuery, onSearch]);

  return (
    <SearchInput
      width="wide"
      placeholder={t("page.calendar.search-reservations.placeholder")}
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  );
}

interface ReservationsContentTabProps {
  search?: string;
  upcoming: CalendarData<AssetBookingDto>;
  past: CalendarData<AssetBookingDto>;
}
function ReservationsContentTab({ upcoming, past, search }: ReservationsContentTabProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Box padding={{ top: "md" }}>
      <Stack gap="md">
        <span className="text-body-emphasis">{t("page.calendar.reservations.section.upcoming")}</span>
        {upcoming.isLoading && (
          <Inline justify="center" wrap={false}>
            <Spinner size="xs" />
          </Inline>
        )}{" "}
        {!upcoming.isLoading && upcoming.items.length === 0 && (
          <span className="text-caption">
            {search
              ? t("page.calendar.reservations.search.no-results")
              : t("page.calendar.reservations.no-upcoming-reservations")}
          </span>
        )}
        {!upcoming.isLoading && upcoming.items.length > 0 && (
          <>
            <Container fullWidth>
              <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
                {upcoming.items.map((reservation) => (
                  <ReservationCard key={reservation.id} reservation={reservation} />
                ))}
              </Tiles>
            </Container>
            {upcoming.isLoadingMore && (
              <Inline justify="center" wrap={false}>
                <Spinner size="xs" />
              </Inline>
            )}
            {!upcoming.isLoadingMore && upcoming.hasMore && (
              <Button variant="subtle" onClick={upcoming.loadMore} data-testid="upcoming-reservations-show-more">
                {t("page.calendar.reservations.section.show-more")}
              </Button>
            )}
          </>
        )}
        <span className="text-body-emphasis">{t("page.calendar.reservations.section.past")}</span>
        {past.isLoading && (
          <Inline justify="center" wrap={false}>
            <Spinner size="xs" />
          </Inline>
        )}
        {!past.isLoading && past.items.length === 0 && (
          <span className="text-caption">
            {search
              ? t("page.calendar.reservations.search.no-results")
              : t("page.calendar.reservations.no-past-reservations")}
          </span>
        )}
        {!past.isLoading && past.items.length > 0 && (
          <>
            <Container fullWidth>
              <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
                {past.items.map((reservation) => (
                  <ReservationCard key={reservation.id} reservation={reservation} isPast />
                ))}
              </Tiles>
            </Container>
            {past.isLoadingMore && (
              <Inline justify="center" wrap={false}>
                <Spinner size="xs" />
              </Inline>
            )}
            {!past.isLoadingMore && past.hasMore && (
              <Button variant="subtle" onClick={past.loadMore}>
                {t("page.calendar.events.section.show-more")}
              </Button>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
